export const SUPER_ADMIN_EMAILS: string[] = ["ad@lg.app", "king@lg.app", "ken@lg.app", "it@lg.app"];
export const LEGAL_EMAILS: string[] = ["kc@lg.app", "lg@lg.app"];
export const SUPERVISOR_OPS_EMAILS: string[] = ["ops@lg.app"];
export const SUPERVISOR_GM_EMAILS: string[] = ["gm@lg.app"];
export const SEAL_EMAILS: string[] = ["seal@lg.app"];

// The Specific Department Accounts
export const DEPT_MINIMAL_EMAILS: string[] = [
  "gm@lg.app",  // 總經理部
  "fa@lg.app",  // 財務部
  "ap@lg.app",  // 行政人事部
  "it@lg.app",  // 資訊部 (Also Super)
  "lg@lg.app",  // 法務部
  "sl1@lg.app", // 業務1部
  "sl2@lg.app", // 業務2部
  "li@lg.app",  // 物流部
  "pt@lg.app",  // 採購部
  "qc@lg.app",  // 品保部
  "md@lg.app",  // 行銷設計部
];

// Accounts to be Temporarily Suspended
export const SUSPENDED_EMAILS: string[] = [];

// Super Users who can view ALL data regardless of department
export const SUPER_USER_EMAILS: string[] = [
  "ad@lg.app",
  "kc@lg.app",
  "ops@lg.app",
  "coo@lg.app",
  "gm@lg.app",
  "psd@lg.app",
  "king@lg.app",
  "ken@lg.app",
  "it@lg.app",
  "js@lg.app" // Pkg: Super Read-only
];

// Pkg: Global check for readonly super users
export function isReadonlySuperUser(email?: string | null): boolean {
  if (!email) return false;
  return email.toLowerCase() === "js@lg.app";
}

// ★ SENSITIVE DATA ACCESS: Tasks, Work Logs, Audit, Operations
// STRICTLY LIMITED TO KC and AD ONLY.
export const SENSITIVE_DATA_VIEWERS: string[] = [
  "kc@lg.app", 
  "ad@lg.app"
];

export const WORKLOG_VIEWER_EMAILS = SENSITIVE_DATA_VIEWERS;
export const AUDIT_VIEWER_EMAILS = SENSITIVE_DATA_VIEWERS;

// Debug View Allowed
export const ALLOWED_DEBUG_VIEWERS: string[] = ["ad@lg.app", "kc@lg.app", "it@lg.app", "king@lg.app", "ken@lg.app"];

// Dept Codes for Debug
export const DEBUG_VIEW_DEPT_CODES: string[] = [
  "GM", "FA", "AP", "IT", "LG", "SL1", "SL2", "LI", "PT", "QC", "MD"
];

// ★ Special Permission: Delete Compliance Projects
export const PROJECT_DELETION_ALLOWED_EMAILS = ["kc@lg.app", "ad@lg.app"];

export function canDeleteComplianceProject(email?: string | null): boolean {
  if (!email) return false;
  if (isReadonlySuperUser(email)) return false; // Pkg: Block readonly super
  return PROJECT_DELETION_ALLOWED_EMAILS.includes(email.toLowerCase());
}

export type UserRole = 'superAdmin' | 'legal' | 'executive' | 'sealUser' | 'staff' | 'viewer' | 'dept-minimal' | 'superViewer' | 'legacyDataEntry';

export const ROLE_LABELS: Record<UserRole, string> = {
  superAdmin: "系統管理員",
  legal: "法務",
  executive: "主管",
  sealUser: "申請人",
  staff: "員工",
  viewer: "訪客",
  "dept-minimal": "部門帳號",
  superViewer: "全域檢視者",
  legacyDataEntry: "舊合約建檔 PT"
};

export function getRoleByEmail(email?: string | null): UserRole {
  if (!email) return 'viewer';
  const lower = email.toLowerCase();
  
  if (SUPER_ADMIN_EMAILS.some(e => lower === e)) return 'superAdmin';
  if (lower === 'js@lg.app') return 'legal'; // Assigned 'legal' for global read scope
  
  // Check if it's a department account but NOT a super admin
  if (DEPT_MINIMAL_EMAILS.some(e => lower === e) && !SUPER_ADMIN_EMAILS.includes(lower)) return 'dept-minimal';
  
  if (SUPERVISOR_OPS_EMAILS.some(e => lower === e)) return 'executive';
  if (SUPERVISOR_GM_EMAILS.some(e => lower === e)) return 'executive';
  
  if (LEGAL_EMAILS.some(e => lower === e)) return 'legal'; 
  
  if (lower === 'ptkeyin@lg.app') return 'legacyDataEntry';

  return 'staff';
}

export function isUserSuspended(email?: string | null): boolean {
  if (!email) return false;
  return SUSPENDED_EMAILS.includes(email.toLowerCase());
}

export function getDeptCodeByEmail(email?: string | null): string {
  if (!email) return "";
  const prefix = email.split("@")[0].toLowerCase();
  
  const map: Record<string, string> = {
    "gm": "GM", "fa": "FA", "ap": "AP", "it": "IT", "lg": "LG",
    "sl1": "SL1", "sl2": "SL2", "li": "LI", "pt": "PT", "qc": "QC", "md": "MD",
    "king": "SYS", "ken": "SYS", "kc": "LG", "ad": "LG", "kevin": "LG", "ptkeyin": "LG",
    "js": "LG"
  };
  
  return map[prefix] || "OTHER";
}

export function isSuperUser(email?: string | null): boolean {
  if (!email) return false;
  return SUPER_USER_EMAILS.includes(email.toLowerCase());
}

export function canInitiateLegalRequest(role: UserRole, email?: string): boolean {
  if (email && isReadonlySuperUser(email)) return false; // Pkg: Block JS
  if (role === 'superViewer') return false;
  return (
    role === 'superAdmin' ||
    role === 'legal' ||
    role === 'executive' ||
    role === 'sealUser' ||
    role === 'dept-minimal'
  );
}

export function isGovAdmin(email?: string | null): boolean {
  if (!email) return false;
  return SUPER_USER_EMAILS.includes(email.toLowerCase());
}

export function canManageAllRequests(email?: string | null): boolean {
  if (email && isReadonlySuperUser(email)) return false; // Pkg: Block JS
  return isGovAdmin(email);
}

export function canViewAllData(email?: string | null): boolean {
  return isSuperUser(email);
}

export function canViewWorklog(email?: string): boolean {
  if (!email) return false;
  return SENSITIVE_DATA_VIEWERS.includes(email.toLowerCase());
}

export function canViewAuditLog(email?: string): boolean {
  if (!email) return false;
  return SENSITIVE_DATA_VIEWERS.includes(email.toLowerCase());
}

export function canViewAuditLogAdmin(email?: string): boolean {
  if (!email) return false;
  return ["ad@lg.app", "kc@lg.app"].includes(email.toLowerCase());
}

export function canUseDebugView(email?: string | null): boolean {
  if (!email) return false;
  return ALLOWED_DEBUG_VIEWERS.includes(email.toLowerCase());
}

export type ModulePermissionKey = 
  | "TASKS_AND_LOG" | "CONTRACTS" | "CASES" | "IP" | "BRAND_ASSETS" | "GOV_FILES" 
  | "TEMPLATES" | "GOV_CORRESPONDENCE" | "LAWYER" | "SIGN_SYSTEM" | "AUDIT"
  | "GOV_DOCS" | "GOV_PROJECTS" | "REG_COMPLIANCE" | "REG_IMPACT_TRACKING" | "OTHERS" | "LEGACY_ENTRY";

export function canEditModule(role: UserRole, module: ModulePermissionKey, email?: string): boolean {
  if (email && isReadonlySuperUser(email)) return false; // Pkg: Forced global block
  if (role === 'legacyDataEntry') return module === 'LEGACY_ENTRY';
  if (role === 'superAdmin' || role === 'legal') return true; 
  if (role === 'executive') return (module === 'SIGN_SYSTEM');
  if (role === 'dept-minimal') return (module === 'SIGN_SYSTEM' || module === 'REG_IMPACT_TRACKING');
  return false; 
}

export function canSeeModule(role: UserRole, module: ModulePermissionKey, email?: string): boolean {
  if (role === 'legacyDataEntry') return module === 'LEGACY_ENTRY';
  if (role === 'superAdmin' || role === 'legal') return true;
  if (role === 'executive') return !(['TASKS_AND_LOG', 'AUDIT'].includes(module));

  if (role === 'dept-minimal') {
    // 嚴格限制四大功能
    const allowed: ModulePermissionKey[] = [
      "SIGN_SYSTEM",           // 用印申請
      "GOV_FILES",             // 公司治理核心文件
      "REG_IMPACT_TRACKING",   // 法規情報及法定專案
      "CONTRACTS"              // 合約草案審查
    ];
    return allowed.includes(module);
  }

  return true;
}

export function isSuperAdmin(email?: string | null) { return getRoleByEmail(email) === 'superAdmin'; }
export function isLegal(email?: string | null) { return email && (LEGAL_EMAILS.includes(email.toLowerCase()) || isSuperAdmin(email) || email.toLowerCase() === 'js@lg.app'); }
export function isManager(email?: string | null) { const r = getRoleByEmail(email); return r === 'executive' || r === 'superAdmin'; }
export function isOps(email?: string | null) { return email && (SUPERVISOR_OPS_EMAILS.includes(email.toLowerCase()) || isSuperAdmin(email)); }
export function isGm(email?: string | null) { return email && (SUPERVISOR_GM_EMAILS.includes(email.toLowerCase()) || isSuperAdmin(email)); }
export function isDeptMinimal(email?: string | null) { return getRoleByEmail(email) === 'dept-minimal'; }

// 合約審查「歷史紀錄」刪除白名單（只限 kc/ad）
export function canDeleteContractReviewHistory(email?: string | null): boolean {
  if (!email) return false;
  const e = String(email).toLowerCase();
  return e === "kc@lg.app" || e === "ad@lg.app";
}

